(() => {
  'use strict';

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class PdpCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;

      this.track = this.querySelector('[data-pdp-carousel-track]');
      this.items = Array.from(this.querySelectorAll('[data-pdp-carousel-item]'));
      this.previousButton = this.querySelector('[data-pdp-carousel-prev]');
      this.nextButton = this.querySelector('[data-pdp-carousel-next]');
      this.dots = Array.from(this.querySelectorAll('[data-pdp-carousel-dot]'));
      this.raf = 0;

      if (!this.track || this.items.length === 0) return;

      this.onScroll = () => {
        window.cancelAnimationFrame(this.raf);
        this.raf = window.requestAnimationFrame(() => this.update());
      };
      this.onPrevious = () => this.goTo(this.currentIndex() - 1);
      this.onNext = () => this.goTo(this.currentIndex() + 1);
      this.onResize = () => this.update();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index) || 0));
      });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.dataset.initialised = 'true';
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.raf);
    }

    currentIndex() {
      if (!this.track || this.items.length === 0) return 0;
      const target = this.track.scrollLeft + 8;
      return this.items.reduce((closest, item, index) => {
        const distance = Math.abs(item.offsetLeft - this.track.offsetLeft - target);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    }

    goTo(index) {
      const targetIndex = Math.max(0, Math.min(index, this.items.length - 1));
      const item = this.items[targetIndex];
      if (!item || !this.track) return;
      this.track.scrollTo({
        left: item.offsetLeft - this.track.offsetLeft,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    update() {
      const index = this.currentIndex();
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }

  class PdpHotspots extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;

      this.buttons = Array.from(this.querySelectorAll('[data-pdp-hotspot-button]'));
      this.mobileOutput = this.querySelector('[data-pdp-hotspot-mobile-output]');
      this.panels = this.buttons
        .map((button) => this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`))
        .filter(Boolean);

      this.panels.forEach((panel) => {
        panel.pdpHome = panel.parentElement;
        panel.pdpNext = panel.nextSibling;
      });

      this.onClick = (event) => {
        const button = event.target.closest('[data-pdp-hotspot-button]');
        if (!button || !this.contains(button)) return;
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        this.closeAll();
        if (willOpen) this.open(button);
      };
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape') return;
        const openButton = this.buttons.find((button) => button.getAttribute('aria-expanded') === 'true');
        if (openButton) {
          this.closeAll();
          openButton.focus();
        }
      };
      this.onResize = () => this.closeAll();

      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.dataset.initialised = 'true';
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
    }

    restore(panel) {
      if (!panel?.pdpHome || panel.parentElement === panel.pdpHome) return;
      panel.pdpHome.insertBefore(panel, panel.pdpNext);
    }

    closeAll() {
      this.buttons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((panel) => {
        panel.hidden = true;
        this.restore(panel);
      });
    }

    open(button) {
      const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
      if (!panel) return;
      if (window.matchMedia('(max-width: 749px)').matches && this.mobileOutput) {
        this.mobileOutput.appendChild(panel);
      }
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
    }
  }

  class PdpDeferredVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;
      this.openButton = this.querySelector('[data-pdp-video-open]');
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('[data-pdp-video-player]');
      this.template = this.querySelector('template[data-pdp-video-template]');
      this.externalUrl = this.dataset.externalVideo || '';

      if (!this.openButton || !this.dialog || !this.player) return;

      this.open = () => {
        this.mountPlayer();
        this.dialog.showModal();
      };
      this.close = () => {
        this.player.replaceChildren();
        this.openButton.focus();
      };
      this.openButton.addEventListener('click', this.open);
      this.dialog.addEventListener('close', this.close);
      this.dataset.initialised = 'true';
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.open);
      this.dialog?.removeEventListener('close', this.close);
    }

    embedUrl(rawUrl) {
      if (!rawUrl) return '';
      try {
        const url = new URL(rawUrl, window.location.origin);
        if (url.hostname.includes('youtu.be')) {
          return `https://www.youtube-nocookie.com/embed/${url.pathname.replace('/', '')}?autoplay=1`;
        }
        if (url.hostname.includes('youtube.com')) {
          const id = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
          return id ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1` : '';
        }
        if (url.hostname.includes('vimeo.com')) {
          const id = url.pathname.split('/').filter(Boolean).pop();
          return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : '';
        }
      } catch (error) {
        return '';
      }
      return '';
    }

    mountPlayer() {
      this.player.replaceChildren();
      if (this.template) {
        this.player.appendChild(this.template.content.cloneNode(true));
        const video = this.player.querySelector('video');
        video?.play().catch(() => {});
        return;
      }
      const source = this.embedUrl(this.externalUrl);
      if (!source) return;
      const iframe = document.createElement('iframe');
      iframe.src = source;
      iframe.title = this.dataset.videoTitle || 'Product video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      this.player.appendChild(iframe);
    }
  }

  if (!customElements.get('pdp-carousel')) customElements.define('pdp-carousel', PdpCarousel);
  if (!customElements.get('pdp-hotspots')) customElements.define('pdp-hotspots', PdpHotspots);
  if (!customElements.get('pdp-deferred-video')) {
    customElements.define('pdp-deferred-video', PdpDeferredVideo);
  }

  if (!window.__lilycutePdpLongformBuyboxLink) {
    window.__lilycutePdpLongformBuyboxLink = true;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-pdp-buybox-link]');
      if (!link) return;
      const productInfo = document.querySelector('[data-lc-premium-product] [id^="ProductInfo-"]');
      const focusTarget =
        productInfo?.querySelector('input:not([type="hidden"]), select, button[name="add"], [data-add-to-cart]');
      if (!productInfo) return;
      event.preventDefault();
      productInfo.scrollIntoView({
        behavior: reducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
      window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), reducedMotion() ? 0 : 500);
    });
  }
})();
